import * as React from "react";
import { makeStyles } from "@fluentui/react-components";
import SignIn, { MatrixCreds } from "./SignIn";
import MeetingTabs from "./MeetingTabs";
import { createClient } from "matrix-js-sdk"; // for whoami check

const useStyles = makeStyles({
  root: { minHeight: "100vh", display: "flex", flexDirection: "column", height: "100%" },
});

const STORAGE_KEY = "salax-matrix-creds";

// --- Storage Helpers ---
export function saveCreds(creds: any) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(creds));
}

export function loadCreds(): any | null {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    return data ? JSON.parse(data) : null;
  } catch {
    return null;
  }
}

function getShortId(roomId) {
  if (!roomId) return "";
  return roomId.startsWith("!") ? roomId.slice(1, 5) : roomId.slice(0, 4);
}
function getMeetingDateString(date = new Date()) {
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = String(date.getFullYear()).slice(-2);
  return `${day}.${month}.${year}`;
}


export function clearCreds() {
  localStorage.removeItem(STORAGE_KEY);
}

// --- Main App Component ---
export default function App(): JSX.Element {
  const css = useStyles();

  // Try loading credentials once at start
  const [creds, setCreds] = React.useState<MatrixCreds | null>(() => loadCreds());
  const [checking, setChecking] = React.useState(true);
  const [meetingTitle, setMeetingTitle] = React.useState<string>("");

  React.useEffect(() => {
    // Only run in Office context
    if (!(window as any).Office || !(window as any).Office.context?.mailbox?.item) return;

    let cancelled = false;
    function fetchSubject() {
      (window as any).Office.context.mailbox.item.subject.getAsync((result: any) => {
        if (!cancelled && result.status === (window as any).Office.AsyncResultStatus.Succeeded) {
          setMeetingTitle(result.value || "");
        }
      });
    }
    // Initial fetch
    fetchSubject();
    // Poll every second for live updates
    const interval = setInterval(fetchSubject, 1000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  // Check if stored credentials are still valid on mount
  React.useEffect(() => {
    if (!creds) {
      setChecking(false);
      return;
    }
    let cancelled = false;

    (async () => {
      try {
        // Lightweight Matrix access token check
        const client = createClient({
          baseUrl: creds.homeserverUrl,
          accessToken: creds.accessToken,
          userId: creds.userId,
          deviceId: creds.deviceId,
        });
        await client.whoami();
        if (!cancelled) setChecking(false);
      } catch (err) {
        clearCreds();
        setCreds(null);
        setChecking(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line
  }, []);

  // Sign out handler
  const handleSignOut = () => {
    setCreds(null);
    clearCreds();
    if (window.Office && Office.context && Office.context.roamingSettings) {
      Office.context.roamingSettings.remove("salaxCreds");
      Office.context.roamingSettings.saveAsync();
    }
  };

  // When the user successfully signs in, save creds to storage
  const handleSignInSuccess = (c: MatrixCreds) => {
    setCreds(c);
    saveCreds(c);
    //console.log("Credentials saved:", c);

    if (window.Office && Office.context && Office.context.roamingSettings) {
      Office.context.roamingSettings.set("salaxCreds", JSON.stringify(c));
      Office.context.roamingSettings.saveAsync();
    }
  };

// Callback to update Outlook meeting body (DOM-based replace)
const handleRoomLinkSelected = ({
  publicLink,
  organizerLink,
  roomId,
  isFromExisting,
  roomName,
}: {
  publicLink?: string;
  organizerLink?: string;
  roomId?: string;
  isFromExisting?: boolean;
  roomName?: string;
}) => {
  if (!(window.Office && Office.context?.mailbox?.item?.body)) {
    console.log({ publicLink, organizerLink });
    return;
  }

  // ---------- helpers ----------
  const escapeHtml = (s = "") =>
    s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!));

  const getFinalRoomName = (): string => {
    if (isFromExisting && roomName) return roomName;
    const id = roomId ? (roomId.startsWith("!") ? roomId.slice(1, 5) : roomId.slice(0, 4)) : "";
    const d = new Date();
    const dateStr = `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(
      d.getFullYear(),
    ).slice(-2)}`;
    return `${id} - ${dateStr}`;
  };

  const buildSalaxBlockHTML = (pub: string, org?: string, finalName?: string): string => {
    const safePub = escapeHtml(pub);
    const safeOrg = org ? escapeHtml(org) : "";
    const safeName = finalName ? escapeHtml(finalName) : "";

    // mark with a resilient data-attribute and an id; this is to resolve the issue on Outlook Mac where it tends to strip out classes and comments
    return `
<div data-salax-block="1" id="salax-meeting-block" style="font-family:'Segoe UI',Arial,sans-serif;margin-top:16px">
  <hr style="border:none;border-top:2px solid #eaeaea;margin:24px 0;">
  <div style="font-size:26px;font-weight:700;margin-bottom:12px;">SalaX Secure Messaging</div>
  <a href="${safePub}" target="_blank"
     style="text-decoration:underline;display:inline-block;margin-bottom:14px;font-size:23px;font-weight:700;letter-spacing:.2px;line-height:2.2;color:#0078d4;">
    Join the meeting now
  </a>
  ${
    safeOrg
      ? `<div style="margin-top:7px;font-size:15px;">
      for meeting organizers:
      <a href="${safeOrg}" target="_blank" style="text-decoration:underline;color:#0078d4;">start the meeting</a>
    </div>`
      : ``
  }
  ${safeName ? `<br><span style="font-size:15px;">Video Room Name: ${safeName}</span>` : ``}
</div>`;
  };

  // Remove any existing SalaX block using DOM, with multiple resilient strategies.
  const stripSalaxBlockDOM = (html: string): { html: string; removed: boolean } => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    let removed = false;

    // 1) Preferred: data attribute
    doc.querySelectorAll<HTMLElement>('[data-salax-block]').forEach((el) => {
      el.remove();
      removed = true;
    });

    // 2) Fallback: id or known class names that may have survived
    if (!removed) {
      doc.querySelectorAll<HTMLElement>("#salax-meeting-block, .salax-meeting-component").forEach((el) => {
        el.remove();
        removed = true;
      });
    }

    // 3) find a container that has the header text AND a "Join the meeting now" link
    if (!removed) {
      const all = Array.from(doc.body.querySelectorAll<HTMLElement>("*"));
      const candidates = all.filter((el) => /SalaX Secure Messaging/i.test(el.textContent || ""));
      for (const node of candidates) {
        let p: HTMLElement | null = node;
        let anchorContainer: HTMLElement | null = null;
        for (let i = 0; i < 6 && p; i += 1, (p = p.parentElement)) {
          const a = p.querySelector("a");
          if (a && /Join\s+the\s+meeting\s+now/i.test(a.textContent || "")) {
            anchorContainer = p;
            break;
          }
        }
        if (anchorContainer) {
          // Also remove a preceding <hr> if present (we add one when building the block)
          const prev = anchorContainer.previousElementSibling;
          if (prev && prev.tagName.toLowerCase() === "hr") prev.remove();
          anchorContainer.remove();
          removed = true;
          break;
        }
      }
    }

    // 4) More defensive remove the sequence between an <hr> that is followed by our header and
    //    the next <hr> (or end). This is tolerant to Outlook re-wrapping.
    if (!removed) {
      const hrs = Array.from(doc.querySelectorAll("hr"));
      for (const hr of hrs) {
        let sib = hr.nextElementSibling as HTMLElement | null;
        const group: HTMLElement[] = [];
        let hasHeader = false;
        while (sib && sib.tagName.toLowerCase() !== "hr") {
          group.push(sib);
          if (/SalaX Secure Messaging/i.test(sib.textContent || "")) hasHeader = true;
          sib = sib.nextElementSibling as HTMLElement | null;
        }
        if (hasHeader) {
          group.forEach((el) => el.remove());
          removed = true;
          break;
        }
      }
    }

    return { html: doc.body.innerHTML, removed };
  };

  // ---------- subject & location ----------
  let finalRoomName = getFinalRoomName();
  const meetingTitle = isFromExisting ? "" : `SalaX Meeting – ${finalRoomName}`;

  if (meetingTitle) {
    Office.context.mailbox.item.subject.setAsync(meetingTitle, () => {});
  }
  if (publicLink) {
    Office.context.mailbox.item.location.setAsync(publicLink, () => {});
  }

  // ---------- read -> strip -> (maybe) append -> write ----------
  Office.context.mailbox.item.body.getAsync(Office.CoercionType.Html, (res) => {
    if (res.status !== Office.AsyncResultStatus.Succeeded) {
      console.error("Failed to get body:", res.error);
      return;
    }

    const currentHtml = res.value || "";
    const { html: userOnlyHtml } = stripSalaxBlockDOM(currentHtml);

    // If no links provided, just write back user content and (optionally) clear subject/location
    if (!publicLink && !organizerLink) {
      Office.context.mailbox.item.body.setAsync(
        userOnlyHtml,
        { coercionType: Office.CoercionType.Html },
        (r2) => {
          if (r2.status !== Office.AsyncResultStatus.Succeeded) {
            console.error("Failed to update meeting body:", r2.error);
          }
        },
      );
      Office.context.mailbox.item.subject.setAsync("", () => {});
      Office.context.mailbox.item.location.setAsync("", () => {});
      return;
    }

    // If not from existing, update finalRoomName to match the name created
    if (!isFromExisting) {
      finalRoomName = roomName;
    }
    // Append fresh block
    const blockHtml = buildSalaxBlockHTML(publicLink!, organizerLink, finalRoomName);
    const nextHtml = userOnlyHtml.trim().length ? `${userOnlyHtml}\n${blockHtml}` : blockHtml;

    Office.context.mailbox.item.body.setAsync(
      nextHtml,
      { coercionType: Office.CoercionType.Html },
      (r2) => {
        if (r2.status !== Office.AsyncResultStatus.Succeeded) {
          console.error("Failed to update meeting body:", r2.error);
        }
      },
    );
  });
};




  React.useEffect(() => {
    if (creds) {
      console.log("Matrix credentials updated:", creds);
    }
  }, [creds]);

  if (checking) {
    return (
      <div className={css.root} style={{ padding: 40, textAlign: "center" }}>
        Loading…
      </div>
    );
  }

  return (
    <div className={css.root}>
      {creds ? (
        <MeetingTabs
          creds={creds}
          onSignOut={handleSignOut}
          onRoomLinkSelected={handleRoomLinkSelected}
          meetingTitle={meetingTitle}
        />
      ) : (
        <SignIn onSuccess={handleSignInSuccess} />
      )}
    </div>
  );
}

